import moment from 'moment';
import format from 'string-template';

import texts from '../texts.json';

import {
  FontFamily,
  circlePath,
  drawCover,
  drawRotated,
  fillSpacedText,
  fillTextOnCircle,
  font,
  measureSpacedText,
  roundedRectPath,
  setFittedFont,
} from './canvasDrawing';
import { getUpcomingStartDate } from './shareCard';

/*
  The frames a client can put their photo in, drawn as in the design: each one fills a 280 × 400 sticker, in the
  design's own units, and the caller scales the canvas to the size it needs. Adding a frame is one entry here plus
  its label in texts.json.

  Without a photo, a frame shows where the photo goes, so the client sees the sticker before they add one.
*/

const STICKER_WIDTH = 280;
const STICKER_HEIGHT = 400;

// Keeps more of the top of the photo, where faces usually are.
const PHOTO_FOCUS_Y = 0.2;
const PHOTO_BACKDROP = '#3A1A1D';
const PLACEHOLDER_FILL = '#F5ECE8';
const PLACEHOLDER_EDGE = '#D6BDB6';
const MUTED_INK = '#6B4E4B';
const BOLD_SHADE = 'rgba(31, 5, 7, 0.88)';
const BOLD_SHADE_CLEAR = 'rgba(31, 5, 7, 0)';
const CAMERA_ICON = 'M4 8h3l2-3h6l2 3h3v11H4z';

// The torn ends of the polaroid's tape, as [x, y] percentages of the strip.
const TAPE_OUTLINE = [
  [0, 6], [4, 0], [8, 9], [3, 22], [7, 34], [2, 48], [6, 61], [1, 75], [5, 88], [2, 100],
  [98, 100], [94, 90], [99, 77], [95, 63], [100, 50], [96, 37], [99, 24], [95, 11], [100, 0], [96, 4],
];

const getDurationUnit = (card) => card.durationUnitLabel.toUpperCase();

const drawPlaceholder = (ctx, {
  x,
  y,
  width,
  height,
  palette,
}) => {
  ctx.fillStyle = PLACEHOLDER_FILL;
  ctx.fillRect(x, y, width, height);
  ctx.setLineDash([6, 4]);
  ctx.lineWidth = 2;
  ctx.strokeStyle = PLACEHOLDER_EDGE;
  ctx.strokeRect(x + 1, y + 1, width - 2, height - 2);
  ctx.setLineDash([]);

  const centerX = x + width / 2;
  const iconY = y + height / 2 - 18;
  circlePath(ctx, centerX, iconY, 32);
  ctx.fillStyle = palette.accent;
  ctx.fill();

  ctx.save();
  ctx.translate(centerX - 15, iconY - 15);
  ctx.scale(30 / 24, 30 / 24);
  ctx.lineWidth = 2;
  ctx.lineJoin = 'round';
  ctx.strokeStyle = palette.onAccent;
  ctx.stroke(new Path2D(CAMERA_ICON));
  circlePath(ctx, 12, 13, 3.5);
  ctx.stroke();
  ctx.restore();

  ctx.fillStyle = palette.ink;
  ctx.font = font('bold', 20);
  ctx.textAlign = 'center';
  ctx.fillText(texts.story.addPhoto, centerX, iconY + 63);
};

// The photo, cropped to the box, or the placeholder when there is none yet.
const drawPhoto = (ctx, {
  photo,
  x,
  y,
  width,
  height,
  radius = 0,
  palette,
}) => {
  ctx.save();
  roundedRectPath(ctx, x, y, width, height, radius);
  ctx.clip();
  if (photo) {
    ctx.fillStyle = PHOTO_BACKDROP;
    ctx.fillRect(x, y, width, height);
    drawCover(ctx, photo, x, y, width, height, PHOTO_FOCUS_Y);
  } else {
    drawPlaceholder(ctx, {
      x,
      y,
      width,
      height,
      palette,
    });
  }
  ctx.restore();
};

// Punches holes in what is already drawn, as a ticket's notches or a stamp's perforations.
const punchHoles = (ctx, holes, radius) => {
  ctx.save();
  ctx.globalCompositeOperation = 'destination-out';
  holes.forEach(([x, y]) => {
    circlePath(ctx, x, y, radius);
    ctx.fill();
  });
  ctx.restore();
};

const drawDashedLine = (ctx, {
  from,
  to,
  color,
  width,
  dash,
}) => {
  ctx.save();
  ctx.globalAlpha = 0.35;
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.setLineDash(dash);
  ctx.beginPath();
  ctx.moveTo(...from);
  ctx.lineTo(...to);
  ctx.stroke();
  ctx.restore();
};

const drawTape = (ctx, palette) => {
  const width = 108;
  const height = 34;
  const left = (STICKER_WIDTH - width) / 2;
  const top = -16;
  drawRotated(ctx, { x: STICKER_WIDTH / 2, y: top + height / 2, degrees: -3 }, () => {
    ctx.beginPath();
    TAPE_OUTLINE.forEach(([px, py]) => ctx.lineTo(left + (px / 100) * width, top + (py / 100) * height));
    ctx.closePath();
    ctx.globalAlpha = 0.86;
    ctx.fillStyle = palette.accent;
    ctx.fill();
    const shine = ctx.createLinearGradient(0, top, 0, top + height);
    shine.addColorStop(0, 'rgba(255, 255, 255, 0.28)');
    shine.addColorStop(0.4, 'rgba(255, 255, 255, 0)');
    shine.addColorStop(1, 'rgba(0, 0, 0, 0.06)');
    ctx.fillStyle = shine;
    ctx.fill();
  });
};

const polaroid = {
  id: 'polaroid',
  draw(ctx, { card, photo, palette }) {
    roundedRectPath(ctx, 0, 0, STICKER_WIDTH, 398, 4);
    ctx.fillStyle = palette.paper;
    ctx.fill();
    drawPhoto(ctx, {
      photo,
      x: 12,
      y: 12,
      width: 256,
      height: 290,
      palette,
    });

    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = palette.ink;
    ctx.font = font('bold', 34, FontFamily.DISPLAY);
    ctx.fillText(texts.story.imIn, 16, 350);
    ctx.fillStyle = palette.accentOnPaper;
    ctx.fillText(setFittedFont(ctx, card.challengeName, {
      weight: 'bold',
      size: 13,
      maxWidth: 172,
      minSize: 10,
    }), 16, 372);

    drawRotated(ctx, { x: 232, y: 350, degrees: 4 }, () => {
      roundedRectPath(ctx, 202, 320, 60, 60, 14);
      ctx.fillStyle = palette.accent;
      ctx.fill();
      ctx.fillStyle = palette.onAccent;
      ctx.textAlign = 'center';
      const count = setFittedFont(ctx, String(card.durationCount), { weight: 'bold', size: 27, maxWidth: 52 });
      ctx.fillText(count, 232, 354);
      ctx.font = font('bold', 9);
      fillSpacedText(ctx, getDurationUnit(card), 232, 368, 1.6);
    });

    drawTape(ctx, palette);
  },
};

// A labeled line on the ticket: a small monospaced label over its value.
const drawTicketField = (ctx, {
  label,
  value,
  x,
  y,
  maxWidth,
}) => {
  ctx.textAlign = 'left';
  ctx.globalAlpha = 0.8;
  ctx.font = font('bold', 8.5, FontFamily.MONO);
  fillSpacedText(ctx, label, x, y + 8, 1.4);
  ctx.globalAlpha = 1;
  ctx.fillText(setFittedFont(ctx, value, {
    weight: 'bold',
    size: 12.5,
    maxWidth,
    minSize: 9,
  }), x, y + 22);
};

const ticket = {
  id: 'ticket',
  draw(ctx, {
    card,
    photo,
    palette,
    now,
  }) {
    const notchY = 290;
    roundedRectPath(ctx, 0, 0, STICKER_WIDTH, STICKER_HEIGHT, 18);
    ctx.fillStyle = palette.accent;
    ctx.fill();
    punchHoles(ctx, [[0, notchY], [STICKER_WIDTH, notchY]], 14);

    drawPhoto(ctx, {
      photo,
      x: 14,
      y: 14,
      width: 252,
      height: 260,
      radius: 10,
      palette,
    });

    ctx.font = font('bold', 12);
    const pillWidth = ctx.measureText(texts.story.committed).width + 18;
    roundedRectPath(ctx, 24, 240, pillWidth, 24, 12);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
    ctx.fill();
    ctx.fillStyle = palette.accentOnPaper;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(texts.story.committed, 33, 256);

    drawDashedLine(ctx, {
      from: [18, notchY],
      to: [262, notchY],
      color: palette.onAccent,
      width: 2,
      dash: [6, 4],
    });

    ctx.fillStyle = palette.onAccent;
    ctx.font = font('bold', 9.5, FontFamily.MONO);
    fillSpacedText(ctx, texts.story.admitOne, 18, 313, 1.7);
    ctx.textAlign = 'right';
    const ticketNumber = format(texts.story.ticketNumber, { number: String(card.durationCount).padStart(4, '0') });
    fillSpacedText(ctx, ticketNumber, 262, 313, 1.7);

    const count = String(card.durationCount);
    const unit = getDurationUnit(card);
    ctx.textAlign = 'left';
    ctx.font = font('bold', 10);
    const unitWidth = measureSpacedText(ctx, unit, 2.4);
    ctx.font = font('bold', 50);
    const columnWidth = Math.max(ctx.measureText(count).width, unitWidth);
    ctx.fillText(count, 18, 366);
    ctx.font = font('bold', 10);
    fillSpacedText(ctx, unit, 18, 382, 2.4);

    const dividerX = 18 + columnWidth + 14;
    drawDashedLine(ctx, {
      from: [dividerX, 326],
      to: [dividerX, 384],
      color: palette.onAccent,
      width: 1.5,
      dash: [4, 3],
    });

    const infoX = dividerX + 14;
    const infoWidth = 262 - infoX;
    const startDate = getUpcomingStartDate(card, now);
    const halfWidth = (infoWidth - 8) / 2;
    drawTicketField(ctx, {
      label: texts.story.challenge,
      value: card.challengeName,
      x: infoX,
      y: 326,
      maxWidth: infoWidth,
    });
    drawTicketField(ctx, {
      label: texts.story.coach,
      value: card.coachName,
      x: infoX,
      y: 354,
      maxWidth: startDate ? halfWidth : infoWidth,
    });
    if (startDate) {
      drawTicketField(ctx, {
        label: texts.story.starts,
        value: moment(startDate).format('MMM D'),
        x: infoX + halfWidth + 8,
        y: 354,
        maxWidth: halfWidth,
      });
    }
  },
};

// The ink stamp on the photo: rings, the motto around them, and the challenge length in the middle.
const drawInkStamp = (ctx, {
  x,
  y,
  card,
  palette,
}) => drawRotated(ctx, { x, y, degrees: -13 }, () => {
  ctx.globalAlpha = 0.92;
  ctx.strokeStyle = palette.accent;
  circlePath(ctx, x, y, 39.5);
  ctx.fillStyle = 'rgba(255, 255, 255, 0.18)';
  ctx.fill();
  ctx.lineWidth = 3;
  ctx.stroke();
  ctx.lineWidth = 1.2;
  circlePath(ctx, x, y, 34.9);
  ctx.stroke();
  circlePath(ctx, x, y, 22.7);
  ctx.stroke();

  ctx.fillStyle = palette.accent;
  ctx.font = font('bold', 7.4);
  ctx.textBaseline = 'middle';
  fillTextOnCircle(ctx, texts.story.stampRing, { x, y, radius: 28.8 });
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'center';
  ctx.fillText(setFittedFont(ctx, String(card.durationCount), { weight: 'bold', size: 19, maxWidth: 38 }), x, y + 4);
  ctx.font = font('bold', 6);
  fillSpacedText(ctx, getDurationUnit(card), x, y + 12, 1.2);
});

const stamp = {
  id: 'stamp',
  draw(ctx, {
    card,
    photo,
    palette,
    now,
  }) {
    ctx.fillStyle = palette.paper;
    ctx.fillRect(0, 0, STICKER_WIDTH, STICKER_HEIGHT);
    const holes = [];
    for (let x = 0; x <= STICKER_WIDTH; x += 20) {
      holes.push([x, 0], [x, STICKER_HEIGHT]);
    }
    for (let y = 20; y < STICKER_HEIGHT; y += 20) {
      holes.push([0, y], [STICKER_WIDTH, y]);
    }
    punchHoles(ctx, holes, 6);

    drawPhoto(ctx, {
      photo,
      x: 22,
      y: 22,
      width: 236,
      height: 290,
      palette,
    });
    drawInkStamp(ctx, {
      x: 206,
      y: 260,
      card,
      palette,
    });

    let textRight = 258;
    const startDate = getUpcomingStartDate(card, now);
    if (startDate) {
      const date = moment(startDate).format('MMM D').toUpperCase();
      ctx.fillStyle = MUTED_INK;
      ctx.textAlign = 'right';
      ctx.font = font('bold', 10.5);
      fillSpacedText(ctx, texts.story.starts, 258, 350, 0.84);
      fillSpacedText(ctx, date, 258, 364, 0.84);
      textRight -= Math.max(measureSpacedText(ctx, texts.story.starts, 0.84), measureSpacedText(ctx, date, 0.84)) + 10;
    }

    const maxWidth = textRight - 22;
    ctx.textAlign = 'left';
    ctx.fillStyle = palette.ink;
    ctx.fillText(setFittedFont(ctx, card.challengeName, {
      weight: 'bold',
      size: 20,
      maxWidth,
      minSize: 13,
    }), 22, 346);
    ctx.fillStyle = palette.accentOnPaper;
    ctx.fillText(setFittedFont(ctx, format(texts.story.withCoach, { coachName: card.coachName }), {
      weight: 'bold',
      size: 11,
      maxWidth,
      minSize: 9,
    }), 22, 364);
  },
};

const bold = {
  id: 'bold',
  draw(ctx, { card, photo, palette }) {
    ctx.save();
    roundedRectPath(ctx, 0, 0, STICKER_WIDTH, STICKER_HEIGHT, 24);
    ctx.clip();
    drawPhoto(ctx, {
      photo,
      x: 0,
      y: 0,
      width: STICKER_WIDTH,
      height: STICKER_HEIGHT,
      palette,
    });
    const shade = ctx.createLinearGradient(0, 0, 0, STICKER_HEIGHT);
    shade.addColorStop(0.42, BOLD_SHADE_CLEAR);
    shade.addColorStop(1, BOLD_SHADE);
    ctx.fillStyle = shade;
    ctx.fillRect(0, 0, STICKER_WIDTH, STICKER_HEIGHT);
    // Stroked on the edge and clipped, so 8 units of border show inside, with the photo's corners rounded under it.
    roundedRectPath(ctx, 0, 0, STICKER_WIDTH, STICKER_HEIGHT, 24);
    ctx.lineWidth = 16;
    ctx.strokeStyle = palette.accent;
    ctx.stroke();
    ctx.restore();

    ctx.font = font('bold', 10);
    roundedRectPath(ctx, 14, 14, measureSpacedText(ctx, texts.story.committed, 1.6) + 18, 23, 11.5);
    ctx.fillStyle = palette.accent;
    ctx.fill();
    ctx.fillStyle = palette.onAccent;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    fillSpacedText(ctx, texts.story.committed, 23, 29.5, 1.6);

    const maxWidth = STICKER_WIDTH - 32;
    ctx.fillStyle = palette.paper;
    ctx.fillText(setFittedFont(ctx, String(card.durationCount), { weight: 'bold', size: 54, maxWidth }), 16, 265);
    ctx.fillText(setFittedFont(ctx, `${getDurationUnit(card)}.`, {
      weight: 'bold',
      size: 54,
      maxWidth,
      minSize: 30,
    }), 16, 311);
    [card.challengeName, card.coachName].forEach((line, index) => {
      ctx.fillText(setFittedFont(ctx, line.toUpperCase(), {
        weight: 'bold',
        size: 11,
        maxWidth,
        minSize: 9,
      }), 16, 333 + index * 13);
    });
    ctx.font = font('bold', 24, FontFamily.DISPLAY);
    ctx.fillText(texts.story.imIn, 16, 374);
  },
};

const STICKER_FRAMES = [polaroid, ticket, stamp, bold];

const getFrame = (frameId) => STICKER_FRAMES.find(({ id }) => id === frameId) || STICKER_FRAMES[0];

export {
  STICKER_WIDTH,
  STICKER_HEIGHT,
  STICKER_FRAMES,
  getFrame,
};
